import { useMutation, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { TextInput, View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { uuidv4 } from "@-/com.npmjs.uuid";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  describeHanReward,
  EMPTY_HAN_REWARD,
  formatHanAmount,
  formatHanMilesAmount,
  grantHan,
  HAN_COIN_ICON_CLASS_NAME,
  type HanReward,
  useHanWalletQueryOptions,
} from "@-/dev.improve.haniverse.lib.han-currency";
import { useState } from "react";
import { AdminPanelButton, AdminPanelSection, AdminPanelStatus } from "#admin-panel-section";

const QUICK_HAN_GRANTS = [10, 100, 1000];

type Grant = Pick<HanReward, "dragons" | "han" | "hanMiles">;

export function AdminPanelWalletSection() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const { data: wallet } = useQuery({ ...useHanWalletQueryOptions(), throwOnError: false });
  const [customHanText, setCustomHanText] = useState("");
  const customHan = Number.parseInt(customHanText, 10);
  const grantMutation = useMutation({
    // A `grant` reward row, the ledger entry internal tooling and QA accounts use. Each grant gets
    // its own id, so repeating one adds again rather than deduplicating.
    mutationFn: async (grant: Grant) =>
      grantHan({
        actorDb: actorDb(import.meta.location),
        dragons: grant.dragons,
        han: grant.han,
        hanMiles: grant.hanMiles,
        id: `admin-grant:${uuidv4()}`,
        now: new Date(),
      }),
    mutationKey: ["adminGrantHan"],
  });
  const grant = (value: Grant) => {
    grantMutation.mutate(value);
  };

  const status = (() => {
    switch (grantMutation.status) {
      case "pending": {
        return "Adding…";
      }
      case "error": {
        return grantMutation.error.message;
      }
      case "success": {
        return `Added ${describeHanReward(grantMutation.variables)}`;
      }
      default: {
        return null;
      }
    }
  })();

  return (
    <AdminPanelSection
      description="Grants land in the wallet ledger like any other reward."
      title="Wallet"
    >
      <View className="flex-row items-center gap-1.5" testID="admin-panel-wallet-balance">
        <Icon className={HAN_COIN_ICON_CLASS_NAME} name="HanCashCoin" size={18} />
        <Text className="font-sans text-base font-semibold tabular-nums">
          {wallet === undefined ? "…" : formatHanAmount(wallet.han)}
        </Text>
        {wallet === undefined ? null : (
          <Text className="font-sans text-base font-semibold tabular-nums">
            {` · 龙 ${wallet.dragons} · ✈ ${formatHanMilesAmount(wallet.hanMiles)}`}
          </Text>
        )}
      </View>
      <View className="flex-row flex-wrap gap-2">
        {QUICK_HAN_GRANTS.map((han) => (
          <AdminPanelButton
            accessibilityLabel={`Add ${han} Han`}
            disabled={grantMutation.isPending}
            key={han}
            onPress={() => grant({ ...EMPTY_HAN_REWARD, han })}
            title={`+${formatHanAmount(han)}`}
          />
        ))}
        <AdminPanelButton
          accessibilityLabel="Add 1,000 HanMiles"
          disabled={grantMutation.isPending}
          onPress={() => grant({ ...EMPTY_HAN_REWARD, hanMiles: 1000 })}
          title="+1,000 HanMiles"
        />
        <AdminPanelButton
          accessibilityLabel="Add a dragon"
          disabled={grantMutation.isPending}
          onPress={() => grant({ ...EMPTY_HAN_REWARD, dragons: 1 })}
          title="+1 龙"
        />
      </View>
      <View className="flex-row items-center gap-2">
        <TextInput
          accessibilityLabel="Han amount"
          className="h-9 flex-1 rounded-xs border border-border bg-background px-3 font-sans text-base text-foreground"
          inputMode="numeric"
          keyboardType="number-pad"
          maxLength={9}
          onChangeText={setCustomHanText}
          placeholder="Any amount"
          returnKeyType="done"
          testID="admin-panel-han-input"
          value={customHanText}
        />
        <AdminPanelButton
          disabled={!Number.isInteger(customHan) || customHan <= 0 || grantMutation.isPending}
          onPress={() => {
            grant({ ...EMPTY_HAN_REWARD, han: customHan });
            setCustomHanText("");
          }}
          title="Add Han"
          variant="primary"
        />
      </View>
      {status === null ? null : <AdminPanelStatus isError={grantMutation.isError} text={status} />}
    </AdminPanelSection>
  );
}
